import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

import { Section } from '@/components/primitives/Section';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
// Legacy JS api module — typed via legacy-shims.d.ts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as tenantsApi from '@/api/tenants';

import type { FPATenant } from '../FPASettingsHub';

/**
 * Coerce free-form text into either a number in range, undefined (empty),
 * or a Zod validation error. We accept empty strings on purpose — those
 * decode to `undefined` so the field round-trips through the API as
 * "leave this alone" rather than "clear me".
 */
const optionalLat = z
  .union([z.string(), z.number()])
  .transform((v) => (v === '' || v == null ? undefined : Number(v)))
  .refine(
    (v) => v === undefined || (!Number.isNaN(v) && v >= -90 && v <= 90),
    { message: 'Latitude must be between -90 and 90' },
  );

const optionalLng = z
  .union([z.string(), z.number()])
  .transform((v) => (v === '' || v == null ? undefined : Number(v)))
  .refine(
    (v) => v === undefined || (!Number.isNaN(v) && v >= -180 && v <= 180),
    { message: 'Longitude must be between -180 and 180' },
  );

const ProfileSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  area: z.string().trim().max(200).optional().or(z.literal('')),
  registration_number: z
    .string()
    .trim()
    .max(100)
    .optional()
    .or(z.literal('')),
  contact_info: z.string().trim().max(2000).optional().or(z.literal('')),
  fdi_share_message: z.string().trim().max(1000).optional().or(z.literal('')),
  latitude: optionalLat,
  longitude: optionalLng,
});

type ProfileValues = z.infer<typeof ProfileSchema>;

interface ProfileTabProps {
  tenantId: number;
  tenant: FPATenant | null;
  onSaved: () => Promise<void> | void;
}

export default function ProfileTab({ tenantId, tenant, onSaved }: ProfileTabProps) {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<ProfileValues>({
    resolver: zodResolver(ProfileSchema),
    defaultValues: {
      name: tenant?.name ?? '',
      area: tenant?.area ?? '',
      registration_number: tenant?.registration_number ?? '',
      contact_info: tenant?.contact_info ?? '',
      fdi_share_message: tenant?.fdi_share_message ?? '',
      latitude: tenant?.latitude ?? undefined,
      longitude: tenant?.longitude ?? undefined,
    },
    mode: 'onTouched',
  });

  // Re-seed when the parent finishes loading the tenant — without this
  // the form sticks on the empty defaults from the first render.
  useEffect(() => {
    if (!tenant) return;
    form.reset({
      name: tenant.name ?? '',
      area: tenant.area ?? '',
      registration_number: tenant.registration_number ?? '',
      contact_info: tenant.contact_info ?? '',
      fdi_share_message: tenant.fdi_share_message ?? '',
      latitude: tenant.latitude ?? undefined,
      longitude: tenant.longitude ?? undefined,
    });
    // form.reset's identity is stable; depending on `tenant` is what we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  const onSubmit = async (values: ProfileValues) => {
    setError(null);
    setSubmitting(true);
    try {
      await tenantsApi.updateTenant(tenantId, {
        name: values.name,
        area: values.area || null,
        registration_number: values.registration_number || null,
        contact_info: values.contact_info || null,
        // Send the string (even empty) so clearing the box clears the saved
        // message — the backend treats null as "leave unchanged".
        fdi_share_message: values.fdi_share_message ?? '',
        latitude: values.latitude ?? null,
        longitude: values.longitude ?? null,
      });
      toast.success('FPA profile saved');
      await onSaved();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      const msg = e?.response?.data?.detail || e?.message || 'Save failed';
      setError(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Section
          title="Identity"
          description="How this FPA appears across the system."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel>
                    Name <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g. Tshwane FPA"
                      autoComplete="organization"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="area"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Area</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g. North Region, South Africa"
                    />
                  </FormControl>
                  <FormDescription>
                    Free-form region label. Doesn&apos;t affect map data.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="registration_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Registration number</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g. REG123456" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Section>

        <Section
          title="Contact"
          description="Visible to staff who manage this FPA."
        >
          <FormField
            control={form.control}
            name="contact_info"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contact info</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    rows={4}
                    placeholder={
                      'Email: contact@example.co.za\nPhone: +27 12 345 6789'
                    }
                  />
                </FormControl>
                <FormDescription>
                  Free-form: email addresses, phone numbers, or anything else
                  worth showing alongside this FPA.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </Section>

        <Section
          title="FDI sharing"
          description="Custom message used when someone shares the daily Fire Danger Index."
        >
          <FormField
            control={form.control}
            name="fdi_share_message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>FDI share message</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    rows={3}
                    placeholder={
                      "e.g. Today's fire danger rating for our FPA. Please avoid open flames and report any fires immediately."
                    }
                  />
                </FormControl>
                <FormDescription>
                  Optional. Used as the caption when the FDI is shared. Leave
                  blank to use the default message.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </Section>

        <Section
          title="Map centre"
          description="Where the map zooms to when this FPA is selected. Decimal degrees."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="latitude"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Latitude</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="any"
                      placeholder="-25.624739"
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === '' ? undefined : e.target.value,
                        )
                      }
                      onBlur={field.onBlur}
                      name={field.name}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="longitude"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Longitude</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="any"
                      placeholder="28.359040"
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === '' ? undefined : e.target.value,
                        )
                      }
                      onBlur={field.onBlur}
                      name={field.name}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Section>

        <div className="flex justify-end gap-2">
          <Button type="submit" disabled={submitting} className="gap-2">
            <Save className="h-4 w-4" />
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
